export type PrimePillarId =
  | 'strategy'
  | 'client'
  | 'performance'
  | 'security'
  | 'project'
  | 'communication'
  | 'technical'
  | 'governance';

export interface RatingLevel {
  value: 1 | 2 | 3 | 4 | 5;
  label: string;
  percent: number;
}

export interface CompetenceScale {
  1: string;
  2: string;
  3: string;
  4: string;
  5: string;
}

export interface SubCompetence {
  id: string;
  name: string;
  description: string;
  scale: CompetenceScale;
  skillScore: number;
  willScore: number;
  evidence: string[];
  minRequired: number;
}

export interface PrimeCategory {
  id: PrimePillarId;
  name: string;
  icon: string;
  color: string;
  description: string;
  minScore: number;
  skillWeight: number;
  willWeight: number;
  currentScore: number;
  subCompetences: SubCompetence[];
}

export interface PrimeLevel {
  level: string;
  minScore: number;
  color: string;
  description: string;
  prismeCertification?: string;
  prismeDescription?: string;
}

export interface PrimeTargetPolicy {
  title: string;
  skill: string;
  will: string;
  rationale: string;
  examples: string[];
}

export const ratingLevels: RatingLevel[] = [
  { value: 1, label: 'Initial / à risque', percent: 20 },
  { value: 2, label: 'Partiel / inconstant', percent: 40 },
  { value: 3, label: 'Conforme / autonome', percent: 60 },
  { value: 4, label: 'Structuré / proactif', percent: 80 },
  { value: 5, label: 'Référence / excellence', percent: 100 },
];

export const scoreToPercent = (score: number) => Math.round((score / 5) * 100);

export const weightedScore = (skillScore: number, willScore: number, skillWeight: number, willWeight: number) =>
  Number(((skillScore * skillWeight + willScore * willWeight) / (skillWeight + willWeight)).toFixed(2));

export const weightedPercent = (skillScore: number, willScore: number, skillWeight: number, willWeight: number) =>
  scoreToPercent(weightedScore(skillScore, willScore, skillWeight, willWeight));

const scale = (l1: string, l2: string, l3: string, l4: string, l5: string): CompetenceScale => ({
  1: l1,
  2: l2,
  3: l3,
  4: l4,
  5: l5,
});

const avg = (items: number[]) => Number((items.reduce((sum, score) => sum + score, 0) / items.length).toFixed(2));

const defaultEvidence = ['Observation gestionnaire', 'Livrables', 'Feedback client/interne', 'Données opérationnelles'];

export const primeTargetPolicy: PrimeTargetPolicy = {
  title: 'Cibles réalistes — progression observable, pas perfection théorique',
  skill:
    'La cible Skill doit représenter le prochain niveau réaliste: généralement ASIS + 0.0 à +0.5 sur 5 selon le niveau actuel, la complexité de la compétence et le profil de la personne. Plus on approche de 5, plus la progression devient difficile; 4.0 est déjà un très bon niveau.',
  will:
    'La cible Will peut être plus ambitieuse parce qu’elle dépend davantage de la volonté, de la discipline et de l’engagement. Repère recommandé: minimum 3.5 pour un comportement attendu, top réaliste 4.5. On évite 5.0 par défaut.',
  rationale:
    'Un target n’est pas le niveau parfait ultime. C’est un objectif mobilisateur, atteignable et mesurable pour le prochain cycle de développement.',
  examples: [
    'Skill ASIS 2.8 → Target 3.1 à 3.3 selon complexité.',
    'Skill ASIS 4.0 → Target 4.0 à 4.2; rester à 4.0 peut être pertinent.',
    'Skill ASIS 4.6+ → Target stable ou +0.1 seulement si preuve forte.',
    'Will faible → viser au moins 3.5; Will déjà bon → viser 4.0 à 4.5.',
  ],
};

export const primeLevels: PrimeLevel[] = [
  {
    level: 'Technicien TI N1',
    minScore: 1,
    color: '#FFA500',
    description: 'Fondations techniques et comportementales en développement',
    prismeCertification: '🥉 PRIsME Certifié',
    prismeDescription: 'Comprend le langage PRIsME, les standards BZ et les attentes de base.',
  },
  {
    level: 'Technicien TI N2 / Prime client',
    minScore: 3,
    color: '#0066FF',
    description: 'Autonome, fiable et capable de porter une relation client',
    prismeCertification: '🥈 PRIsME Expert',
    prismeDescription: 'Applique PRIsME avec constance et contribue à l’évolution du client.',
  },
  {
    level: 'Technicien TI N3 / Senior Prime',
    minScore: 4.5,
    color: '#00B894',
    description: 'Référence technique, relationnelle et comportementale',
    prismeCertification: '🥇 PRIsME Maître',
    prismeDescription: 'Enseigne, standardise et élève les autres ressources.',
  },
];

const categoriesSeed: Omit<PrimeCategory, 'currentScore'>[] = [
  {
    id: 'strategy',
    name: 'Stratégie & Vision',
    icon: 'Target',
    color: '#7C3AED',
    description: 'Capacité à structurer, conseiller et aligner les décisions TI avec les objectifs du client.',
    minScore: 3,
    skillWeight: 60,
    willWeight: 40,
    subCompetences: [
      { id: 'plan-directeur', name: 'Plan Directeur TI', description: 'Élabore et fait vivre une vision TI structurée.', scale: scale('Aucun plan / décisions réactives', 'Plan partiel', 'Plan structuré aligné client', 'Ajusté proactivement', 'Influence stratégique client'), skillScore: 4, willScore: 4, minRequired: 3, evidence: ['Plan directeur', 'Décisions documentées', 'Alignement client'] },
      { id: 'roadmap', name: 'Roadmap Annuel', description: 'Structure et suit la roadmap annuelle.', scale: scale('Aucun suivi', 'Suivi irrégulier', 'Suivi structuré', 'Ajusté proactivement', 'Optimisé'), skillScore: 4, willScore: 4, minRequired: 3, evidence: ['Roadmap', 'Révisions trimestrielles', 'Actions complétées'] },
      { id: 'comite-ti', name: 'Comité TI', description: 'Prépare, anime ou contribue efficacement aux comités TI.', scale: scale('Désorganisé', 'Peu structuré', 'Correct', 'Structuré + décisions', 'Forte valeur stratégique'), skillScore: 3, willScore: 4, minRequired: 3, evidence: ['Ordres du jour', 'Décisions', 'Suivis'] },
      { id: 'innovation-roi', name: 'Innovation & ROI', description: 'Identifie des opportunités d’amélioration générant de la valeur.', scale: scale('Aucun apport', 'Réactif', 'Apporte de la valeur', 'Proactif', 'ROI mesurable'), skillScore: 4, willScore: 4, minRequired: 3, evidence: ['Recommandations', 'ROI estimé', 'Améliorations livrées'] },
      { id: 'conseil-strategique', name: 'Conseil Stratégique Client', description: 'Conseille le client avec hauteur et jugement.', scale: scale('Aucun conseil', 'Conseil réactif', 'Conseille adéquatement', 'Anticipe les besoins', 'Partenaire stratégique'), skillScore: 4, willScore: 4, minRequired: 3, evidence: ['Recommandations client', 'Feedback client', 'Décisions influencées'] },
    ],
  },
  {
    id: 'client',
    name: 'Gestion Client & Profil Nova',
    icon: 'Users',
    color: '#2563EB',
    description: 'Qualité de relation, compréhension client et capacité à créer une expérience fiable.',
    minScore: 3,
    skillWeight: 50,
    willWeight: 50,
    subCompetences: [
      { id: 'profil-nova', name: 'Profil Nova Client', description: 'Comprend et adapte sa posture au profil client.', scale: scale('Ne comprend pas', 'Compréhension partielle', 'Compréhension correcte', 'Adapté au client', 'Influence positivement la relation'), skillScore: 3, willScore: 4, minRequired: 3, evidence: defaultEvidence },
      { id: 'connaissance-client', name: 'Connaissance Client', description: 'Maîtrise le contexte, l’historique et les priorités du client.', scale: scale('Aucune', 'Partielle', 'Bonne', 'Très bonne', 'Vision stratégique'), skillScore: 4, willScore: 4, minRequired: 3, evidence: ['Profil client', 'Historique', 'Préparation rencontres'] },
      { id: 'portfolio', name: 'Portfolio Clients', description: 'Organise et maîtrise son portefeuille client.', scale: scale('Désorganisé', 'Partiel', 'Structuré', 'Maîtrisé', 'Optimisé'), skillScore: 4, willScore: 4, minRequired: 3, evidence: ['Liste clients', 'Priorités', 'Suivis'] },
      { id: 'relations-client', name: 'Relations Client', description: 'Maintient une relation professionnelle, stable et proactive.', scale: scale('Conflits fréquents', 'Relation faible', 'Relation professionnelle', 'Relation proactive', 'Confiance forte'), skillScore: 4, willScore: 5, minRequired: 3, evidence: ['Feedback client', 'Renouvellements', 'Escalades'] },
      { id: 'analyse-financiere', name: 'Analyse Financière', description: 'Comprend les impacts financiers de ses recommandations.', scale: scale('Aucune compréhension', 'Basique', 'Correcte', 'Pertinente', 'Optimisée'), skillScore: 3, willScore: 4, minRequired: 3, evidence: ['Budgets', 'Recommandations', 'ROI'] },
      { id: 'experience-client', name: 'Expérience Client', description: 'Crée une expérience client constante, simple et rassurante.', scale: scale('Expérience négative', 'Inconstante', 'Correcte', 'Proactive', 'Excellente'), skillScore: 4, willScore: 5, minRequired: 3, evidence: ['CSAT/NPS', 'Commentaires client', 'Qualité des suivis'] },
    ],
  },
  {
    id: 'performance',
    name: 'Performance & ROI',
    icon: 'TrendingUp',
    color: '#10B981',
    description: 'Mesure, suit et améliore la performance TI avec des indicateurs simples.',
    minScore: 3,
    skillWeight: 60,
    willWeight: 40,
    subCompetences: [
      { id: 'dashboard-spider', name: 'Dashboard Spider', description: 'Utilise des vues visuelles pour comparer ASIS et Target.', scale: scale('Inexistant', 'Faible', 'Correct', 'Pertinent', 'Décisionnel'), skillScore: 4, willScore: 4, minRequired: 3, evidence: ['Dashboard', 'Analyse', 'Présentation'] },
      { id: 'kpi', name: 'KPIs & Métriques', description: 'Définit, suit et explique des KPI utiles.', scale: scale('Non suivis', 'Incomplets', 'Suivis', 'Analysés', 'Optimisés'), skillScore: 4, willScore: 4, minRequired: 3, evidence: ['KPI', 'Rapports', 'Tendances'] },
      { id: 'roi', name: 'Optimisation ROI', description: 'Améliore le retour sur investissement client.', scale: scale('Aucun impact', 'Faible impact', 'Impact correct', 'Amélioration visible', 'ROI démontré'), skillScore: 4, willScore: 4, minRequired: 3, evidence: ['ROI', 'Économies', 'Valeur créée'] },
      { id: 'budget', name: 'Suivi Budgétaire', description: 'Suit les budgets et anticipe les écarts.', scale: scale('Aucun suivi', 'Suivi inconstant', 'Correct', 'Structuré', 'Optimisé'), skillScore: 4, willScore: 4, minRequired: 3, evidence: ['Budget', 'Écarts', 'Prévisions'] },
      { id: 'efficacite-ti', name: 'Efficacité TI', description: 'Respecte SLA, priorités et productivité attendue.', scale: scale('Inefficace / retards', 'Inconstant', 'Respect SLA / priorités', 'Très efficace', 'Optimise les opérations'), skillScore: 4, willScore: 5, minRequired: 3, evidence: ['SLA', 'Tickets', 'Productivité', 'Priorités'] },
      { id: 'satisfaction-overall', name: 'Satisfaction Overall Client', description: 'Contribue à la satisfaction globale du client.', scale: scale('Insatisfaction', 'Variable', 'Correcte', 'Bonne', 'Excellente'), skillScore: 4, willScore: 5, minRequired: 3, evidence: ['CSAT/NPS', 'Feedback', 'Renouvellement'] },
    ],
  },
  {
    id: 'security',
    name: 'Sécurité & Conformité',
    icon: 'Shield',
    color: '#DC2626',
    description: 'Applique les réflexes sécurité et les standards de conformité BZ/client.',
    minScore: 3,
    skillWeight: 60,
    willWeight: 40,
    subCompetences: [
      { id: 'nist', name: 'Cadre NIST 2.0', description: 'Comprend et applique les principes NIST pertinents.', scale: scale('Non appliqué', 'Partiel', 'Conforme', 'Structuré', 'Optimisé'), skillScore: 4, willScore: 4, minRequired: 3, evidence: ['Contrôles', 'Recommandations', 'Documentation'] },
      { id: 'photo-score', name: 'Photo Score Sécurité', description: 'Contribue à l’évaluation sécurité ASIS/Target.', scale: scale('Aucun', 'Faible', 'Correct', 'Structuré', 'Stratégique'), skillScore: 4, willScore: 4, minRequired: 3, evidence: ['Photo score', 'Constats', 'Plan d’action'] },
      { id: 'cadre-bz', name: 'Cadre Référentiel BZ', description: 'Utilise le cadre BZ dans ses recommandations.', scale: scale('Non utilisé', 'Partiel', 'Correct', 'Bien intégré', 'Maîtrise complète'), skillScore: 4, willScore: 4, minRequired: 3, evidence: ['Cadre BZ', 'Livrables', 'Recommandations'] },
      { id: 'cyber-ops', name: 'Cybersécurité Opérationnelle', description: 'Applique les bons réflexes opérationnels de cybersécurité.', scale: scale('Risque élevé', 'Erreurs fréquentes', 'Conforme', 'Bon réflexe', 'Leader'), skillScore: 4, willScore: 4, minRequired: 3, evidence: ['Incidents', 'Contrôles', 'Escalades'] },
      { id: 'conformite', name: 'Gestion de la Conformité', description: 'Respecte les exigences internes, client et réglementaires.', scale: scale('Non-respect', 'Partiel', 'Conforme', 'Structuré', 'Optimisé'), skillScore: 4, willScore: 4, minRequired: 3, evidence: ['Audits', 'Procédures', 'Exceptions'] },
      { id: 'reflexe-securite', name: 'Réflexe Sécurité', description: 'Pense sécurité naturellement avant d’agir.', scale: scale('Comportements à risque', 'Oublis fréquents', 'Applique les règles', 'Vigilant', 'Référence'), skillScore: 4, willScore: 5, minRequired: 3, evidence: ['Signalements', 'Prévention', 'Respect MFA/accès'] },
    ],
  },
  {
    id: 'project',
    name: 'Gestion de Projet',
    icon: 'FolderKanban',
    color: '#F59E0B',
    description: 'Planifie, suit, livre et escalade avec rigueur.',
    minScore: 3,
    skillWeight: 60,
    willWeight: 40,
    subCompetences: [
      { id: 'charge-projet', name: 'Chargé de Projet', description: 'Prend en charge un projet avec méthode.', scale: scale('Gestion inadéquate', 'Inconstant', 'Correct', 'Structuré', 'Excellent'), skillScore: 4, willScore: 4, minRequired: 3, evidence: ['Plans projet', 'Suivis', 'Livraisons'] },
      { id: 'suivi-livrables', name: 'Suivi & Livrables', description: 'Suit les livrables et respecte les engagements.', scale: scale('Retards fréquents', 'Inconstant', 'Respect des engagements', 'Rigoureux', 'Optimisé'), skillScore: 4, willScore: 5, minRequired: 3, evidence: ['Livrables', 'Échéances', 'Qualité'] },
      { id: 'revisions-trimestrielles', name: 'Révisions Trimestrielles', description: 'Prépare et exécute les revues trimestrielles.', scale: scale('Non réalisées', 'Incomplètes', 'Correctes', 'Structurées', 'Optimisées'), skillScore: 4, willScore: 4, minRequired: 3, evidence: ['Revues', 'Actions', 'Décisions'] },
      { id: 'planification', name: 'Planification', description: 'Planifie les efforts, ressources et dépendances.', scale: scale('Désorganisé', 'Partiel', 'Correct', 'Anticipe', 'Optimise les ressources'), skillScore: 4, willScore: 4, minRequired: 3, evidence: ['Planification', 'Capacité', 'Risques'] },
      { id: 'gestion-risques', name: 'Gestion des Risques', description: 'Identifie, qualifie et mitige les risques.', scale: scale('Risques ignorés', 'Réactif', 'Risques identifiés', 'Risques gérés', 'Risques anticipés'), skillScore: 4, willScore: 4, minRequired: 3, evidence: ['Registre risques', 'Mitigations', 'Escalades'] },
      { id: 'communication-ia', name: 'Communication IA-Assistée', description: 'Utilise l’IA pour structurer communications et suivis.', scale: scale('Non utilisée', 'Faible', 'Correcte', 'Structurée', 'Optimisée'), skillScore: 4, willScore: 4, minRequired: 3, evidence: ['Résumés IA', 'Comptes rendus', 'Suivis'] },
    ],
  },
  {
    id: 'communication',
    name: 'Communication Multi-Niveaux',
    icon: 'MessageSquare',
    color: '#CA8A04',
    description: 'Communique clairement avec utilisateurs, équipes techniques, gestionnaires et clients.',
    minScore: 3,
    skillWeight: 40,
    willWeight: 60,
    subCompetences: [
      { id: 'niveau-1', name: 'Communication Niveau 1', description: 'Communique avec les utilisateurs finaux.', scale: scale('Mauvaise', 'Faible', 'Correcte', 'Proactive', 'Excellente'), skillScore: 4, willScore: 5, minRequired: 3, evidence: ['Tickets', 'Feedback utilisateurs', 'Clarté'] },
      { id: 'niveau-2', name: 'Communication Niveau 2', description: 'Coordonne avec les équipes techniques.', scale: scale('Inefficace', 'Difficile', 'Correcte', 'Bonne coordination', 'Excellente'), skillScore: 4, willScore: 5, minRequired: 3, evidence: ['Escalades', 'Transferts', 'Collaboration'] },
      { id: 'niveau-3', name: 'Communication Niveau 3', description: 'Adapte son message aux décideurs et contextes stratégiques.', scale: scale('Non adaptée', 'Faible', 'Correcte', 'Structurée', 'Impact stratégique'), skillScore: 3, willScore: 4, minRequired: 3, evidence: ['Présentations', 'Synthèses', 'Décisions'] },
      { id: 'presentation-client', name: 'Présentation Client', description: 'Présente professionnellement constats, KPI et recommandations.', scale: scale('Non professionnelle', 'Faible', 'Correcte', 'Structurée', 'Impact élevé'), skillScore: 4, willScore: 4, minRequired: 3, evidence: ['Présentations', 'Feedback', 'Décisions'] },
      { id: 'ecoute-active', name: 'Écoute Active', description: 'Écoute, reformule et valide avant de conclure.', scale: scale('N’écoute pas', 'Écoute partielle', 'Correcte', 'Bonne', 'Excellente'), skillScore: 4, willScore: 5, minRequired: 3, evidence: ['Reformulation', 'Besoins validés', 'Feedback'] },
      { id: 'tickets-ecrits', name: 'Qualité des Tickets et Communications Écrites', description: 'Rédige des tickets et communications exploitables.', scale: scale('Inexploitable', 'Incomplet', 'Clair', 'Structuré', 'Référence'), skillScore: 4, willScore: 5, minRequired: 3, evidence: ['Tickets', 'Courriels', 'Notes techniques'] },
      { id: 'collaboration', name: 'Collaboration', description: 'Contribue positivement au collectif.', scale: scale('Impact négatif', 'Peu impliqué', 'Correct', 'Aide les autres', 'Leader collaboratif'), skillScore: 4, willScore: 5, minRequired: 3, evidence: ['Feedback équipe', 'Support pairs', 'Mentorat'] },
    ],
  },
  {
    id: 'technical',
    name: 'Compétences Techniques',
    icon: 'Code',
    color: '#111827',
    description: 'Maîtrise technique utile pour diagnostiquer, résoudre, escalader et conseiller.',
    minScore: 3,
    skillWeight: 60,
    willWeight: 40,
    subCompetences: [
      { id: 'cloud-infra-dev-ia', name: 'Cloud / Infrastructure / Dev / IA', description: 'Comprend les domaines techniques clés de l’environnement client.', scale: scale('Faible', 'Limité', 'Correct', 'Solide', 'Expert'), skillScore: 4, willScore: 4, minRequired: 3, evidence: ['Résolutions', 'Architecture', 'Conseils'] },
      { id: 'veille', name: 'Veille Technologique', description: 'Reste à jour et partage les apprentissages utiles.', scale: scale('Aucune', 'Faible', 'À jour', 'Proactive', 'Référence'), skillScore: 4, willScore: 5, minRequired: 3, evidence: ['Formations', 'Partage', 'Certifications'] },
      { id: 'infogerance', name: 'Infogérance', description: 'Gère efficacement les opérations TI confiées.', scale: scale('Gestion inadéquate', 'Inconstant', 'Correct', 'Efficace', 'Optimisé'), skillScore: 4, willScore: 4, minRequired: 3, evidence: ['SLA', 'Tickets', 'Incidents'] },
      { id: 'incidents', name: 'Gestion des Incidents', description: 'Prend en charge les incidents selon les bonnes pratiques ITIL.', scale: scale('Prise en charge inadéquate', 'Lent', 'Conforme ITIL', 'Efficace', 'Expert'), skillScore: 4, willScore: 4, minRequired: 3, evidence: ['Incidents', 'Temps réponse', 'Post-mortem'] },
      { id: 'diagnostic', name: 'Diagnostic Technique', description: 'Diagnostique rapidement et précisément les problèmes.', scale: scale('Diagnostic inadéquat', 'Lent', 'Correct', 'Rapide', 'Expert'), skillScore: 4, willScore: 4, minRequired: 3, evidence: ['RCA', 'Résolution', 'Escalade'] },
      { id: 'escalade-technique', name: 'Gestion de l’Escalade Technique', description: 'Escalade au bon moment avec le bon niveau d’information.', scale: scale('Inadéquate', 'Mal calibrée', 'Correcte', 'Structurée', 'Référence'), skillScore: 4, willScore: 4, minRequired: 3, evidence: ['Escalades', 'Qualité infos', 'Délais'] },
    ],
  },
  {
    id: 'governance',
    name: 'Gouvernance & Cadre BZ',
    icon: 'Building',
    color: '#64748B',
    description: 'Respecte les standards BZ, documente, apprend et démontre une fiabilité professionnelle.',
    minScore: 3,
    skillWeight: 40,
    willWeight: 60,
    subCompetences: [
      { id: 'asis-target', name: 'Méthodologie ASIS vs Target', description: 'Utilise ASIS/Target pour clarifier les écarts.', scale: scale('Non utilisée', 'Partielle', 'Correcte', 'Structurée', 'Optimisée'), skillScore: 4, willScore: 4, minRequired: 3, evidence: ['ASIS/Target', 'Plans', 'Écarts'] },
      { id: 'vision-holistique', name: 'Vision Holistique BZ', description: 'Comprend l’ensemble du contexte client et BZ.', scale: scale('Faible', 'Partielle', 'Correcte', 'Bonne', 'Maîtrise complète'), skillScore: 4, willScore: 4, minRequired: 3, evidence: ['Recommandations', 'Cadres', 'Décisions'] },
      { id: 'processus-standards', name: 'Processus & Standards', description: 'Respecte les standards et processus attendus.', scale: scale('Non-respect', 'Inconstant', 'Conforme', 'Rigoureux', 'Référence'), skillScore: 4, willScore: 5, minRequired: 3, evidence: ['Audits', 'Respect processus', 'Qualité'] },
      { id: 'animation-comite', name: 'Animation Comité', description: 'Anime ou contribue efficacement aux rencontres de gouvernance.', scale: scale('Inefficace', 'Faible', 'Correcte', 'Structurée', 'Impact élevé'), skillScore: 3, willScore: 4, minRequired: 3, evidence: ['Rencontres', 'Décisions', 'Suivis'] },
      { id: 'documentation', name: 'Documentation & Transparence', description: 'Documente clairement le travail, les décisions et les risques.', scale: scale('Absente', 'Incomplète', 'Correcte', 'Structurée', 'Excellente'), skillScore: 4, willScore: 5, minRequired: 3, evidence: ['Documentation', 'Notes', 'Partage'] },
      { id: 'journaux-temps', name: 'Journaux de Temps', description: 'Saisit le temps avec rigueur et utilité de gestion.', scale: scale('Non faits', 'Incomplets', 'Corrects', 'Rigoureux', 'Exemplaires'), skillScore: 3, willScore: 5, minRequired: 3, evidence: ['Timesheets', 'Descriptions', 'Délais de saisie'] },
      { id: 'amelioration-continue', name: 'Amélioration Continue', description: 'Cherche à s’améliorer et améliore le système autour de lui.', scale: scale('Aucune', 'Réactif', 'Suit les attentes', 'Propose', 'Implémente'), skillScore: 4, willScore: 5, minRequired: 3, evidence: ['Suggestions', 'Kaizen', 'Améliorations livrées'] },
      { id: 'adaptabilite', name: 'Adaptabilité', description: 'S’adapte aux changements sans dégrader la qualité.', scale: scale('Résistance', 'Difficulté', 'S’adapte', 'Flexible', 'Leader'), skillScore: 4, willScore: 5, minRequired: 3, evidence: ['Changements', 'Feedback', 'Résilience'] },
      { id: 'ponctualite-assiduite', name: 'Ponctualité & Assiduité', description: 'Démontre fiabilité, présence et respect des engagements.', scale: scale('Retards fréquents, impacte l’équipe', 'Inconstant', 'Généralement à l’heure', 'Fiable et ponctuel', 'Exemplaire'), skillScore: 3, willScore: 5, minRequired: 3, evidence: ['Présence', 'Ponctualité', 'Fiabilité'] },
    ],
  },
];

export const primeCategories: PrimeCategory[] = categoriesSeed.map((category) => {
  const combinedScores = category.subCompetences.map((sub) =>
    weightedScore(sub.skillScore, sub.willScore, category.skillWeight, category.willWeight),
  );
  return {
    ...category,
    currentScore: avg(combinedScores),
  };
});

export const primeSpiderData = {
  categories: primeCategories.map((cat) => cat.name),
  skillScores: primeCategories.map((cat) => scoreToPercent(avg(cat.subCompetences.map((sub) => sub.skillScore)))),
  willScores: primeCategories.map((cat) => scoreToPercent(avg(cat.subCompetences.map((sub) => sub.willScore)))),
  combinedScores: primeCategories.map((cat) => scoreToPercent(cat.currentScore)),
  minRequired: primeCategories.map((cat) => scoreToPercent(cat.minScore)),
};
